/**
 * Catalog model for the Reports v2 shell: merges the legacy definitions and
 * the new-engine (v2) definitions from GET /report-definitions into one list
 * of entries with category / sub-group / icon / NEW metadata, and derives the
 * sidebar sections, display names and copy names from it.
 *
 * Tolerates a backend that predates the shell: legacy definitions without
 * catalog metadata land in "Legacy", and missing `categories` /
 * `v2_definitions` are synthesised / treated as empty.
 */
import {
  ReportCategory,
  ReportDefinition,
  ReportDefinitionsResponse,
  ReportV2DefinitionSummary,
  SalonReport,
} from "../../../types/reports";
import type { ReportsPermissions } from "../../../types/featureAccess";
import { canOpenReport } from "../../../utils/reportAccess";

export const LEGACY_CATEGORY_KEY = "legacy";
const LEGACY_CATEGORY: ReportCategory = {
  key: LEGACY_CATEGORY_KEY,
  label: "Legacy",
  icon: "history",
};
const DEFAULT_SUB_GROUP = "Reports";

export interface CatalogEntry {
  reportType: string;
  engine: "legacy" | "v2";
  name: string;
  description: string;
  category: string;
  subGroup: string;
  icon: string | null;
  isNew: boolean;
  /** The full legacy definition (rendered by the untouched ReportView). */
  legacyDefinition?: ReportDefinition;
  /** The new-engine definition (rendered by the v2 ReportCanvas). */
  v2Definition?: ReportV2DefinitionSummary;
}

export interface ReportCatalog {
  entries: CatalogEntry[];
  /** Display order; Legacy is always last. */
  categories: ReportCategory[];
  byType: ReadonlyMap<string, CatalogEntry>;
}

const titleCase = (key: string): string =>
  key
    .split(/[_-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

export function buildCatalog(
  response: ReportDefinitionsResponse | null | undefined,
): ReportCatalog {
  const legacy: CatalogEntry[] = (response?.definitions ?? []).map((d) => ({
    reportType: d.report_type,
    engine: "legacy",
    name: d.name,
    description: d.description,
    category: d.category || LEGACY_CATEGORY_KEY,
    subGroup: d.sub_group || DEFAULT_SUB_GROUP,
    icon: d.icon ?? null,
    isNew: false,
    legacyDefinition: d,
  }));
  const v2: CatalogEntry[] = (response?.v2_definitions ?? []).map((d) => ({
    reportType: d.report_type,
    engine: "v2",
    name: d.name,
    description: d.description,
    category: d.category,
    subGroup: d.sub_group || DEFAULT_SUB_GROUP,
    icon: d.icon ?? null,
    isNew: !!d.is_new,
    v2Definition: d,
  }));
  // New-engine entries first so a category lists its new reports ahead of
  // anything else; within an engine, the API order is kept.
  const entries = [...v2, ...legacy];

  const categories: ReportCategory[] = [...(response?.categories ?? [])];
  const known = new Set(categories.map((c) => c.key));
  for (const entry of entries) {
    if (known.has(entry.category)) continue;
    known.add(entry.category);
    categories.push(
      entry.category === LEGACY_CATEGORY_KEY
        ? LEGACY_CATEGORY
        : { key: entry.category, label: titleCase(entry.category) },
    );
  }
  const used = new Set(entries.map((e) => e.category));
  const ordered = categories
    .filter((c) => used.has(c.key))
    .sort(
      (a, b) =>
        Number(a.key === LEGACY_CATEGORY_KEY) -
        Number(b.key === LEGACY_CATEGORY_KEY),
    );

  return {
    entries,
    categories: ordered,
    byType: new Map(entries.map((e) => [e.reportType, e])),
  };
}

/** Category for a saved report; unknown types (definition removed) are legacy. */
export function categoryOf(
  report: SalonReport,
  catalog: ReportCatalog,
): string {
  return (
    catalog.byType.get(report.report_type)?.category ?? LEGACY_CATEGORY_KEY
  );
}

export function reportDisplayName(
  report: SalonReport,
  catalog: ReportCatalog,
): string {
  return (
    report.custom_name ||
    catalog.byType.get(report.report_type)?.name ||
    "Report"
  );
}

/** The saved description, falling back to the catalog's; `""` means the user
 *  cleared it on purpose. */
export function reportDisplayDescription(
  report: SalonReport,
  catalog: ReportCatalog,
): string {
  if (
    report.custom_description !== undefined &&
    report.custom_description !== null
  ) {
    return report.custom_description;
  }
  return catalog.byType.get(report.report_type)?.description ?? "";
}

export interface ReportSection {
  category: ReportCategory;
  reports: SalonReport[];
}

/** The user's reports grouped into sidebar sections in catalog order
 *  (Legacy last), keeping the list order within a section. */
export function groupReportsByCategory(
  reports: SalonReport[],
  catalog: ReportCatalog,
): ReportSection[] {
  const categories = [...catalog.categories];
  if (
    reports.some((r) => categoryOf(r, catalog) === LEGACY_CATEGORY_KEY) &&
    !categories.some((c) => c.key === LEGACY_CATEGORY_KEY)
  ) {
    categories.push(LEGACY_CATEGORY);
  }
  return categories
    .map((category) => ({
      category,
      reports: reports.filter((r) => categoryOf(r, catalog) === category.key),
    }))
    .filter((s) => s.reports.length > 0);
}

/** The first report in sidebar order (sections in catalog order). */
export function firstReportInOrder(
  reports: SalonReport[],
  catalog: ReportCatalog,
): SalonReport | null {
  return groupReportsByCategory(reports, catalog)[0]?.reports[0] ?? null;
}

/** How many saved instances of each report type the user has. */
export function countByType(reports: SalonReport[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const r of reports) {
    counts.set(r.report_type, (counts.get(r.report_type) ?? 0) + 1);
  }
  return counts;
}

/**
 * Number of NEW reports in the catalog — the "N new" pill on Add report.
 * Pass the catalog the picker shows (narrowed to what the user can add), so
 * the pill always equals the picker's What's new count. Already-added
 * reports still count: the pill advertises what's new, not what's missing.
 */
export const newEntryCount = (catalog: ReportCatalog): number =>
  catalog.entries.filter((e) => e.isNew).length;

/** NEW catalog entries the user hasn't added yet (the picker opens on What's
 *  new while there are any). */
export function newEntriesNotInList(
  catalog: ReportCatalog,
  reports: SalonReport[],
): CatalogEntry[] {
  const counts = countByType(reports);
  return catalog.entries.filter((e) => e.isNew && !counts.get(e.reportType));
}

const COPY_SUFFIX = / \((\d+)\)$/;

/**
 * Next free "Name (n)" for a repeat add or a duplicate: strips an existing
 * " (n)" suffix, then picks the smallest n ≥ 2 not already used.
 */
export function nextCopyName(name: string, existingNames: string[]): string {
  const base = name.replace(COPY_SUFFIX, "");
  const taken = new Set(existingNames);
  let n = 2;
  while (taken.has(`${base} (${n})`)) n += 1;
  return `${base} (${n})`;
}

/**
 * The catalog narrowed to the reports the user can run (utils/reportAccess),
 * for the "Add a report" picker. The full catalog stays the source of names
 * for reports already in the list. Unloaded permissions narrow nothing (the
 * API still refuses what isn't allowed).
 */
export function catalogForAccess(
  catalog: ReportCatalog,
  perms: ReportsPermissions | null | undefined,
): ReportCatalog {
  if (!perms) return catalog;
  const entries = catalog.entries.filter((e) =>
    canOpenReport(perms, e.v2Definition ?? e.legacyDefinition ?? {}),
  );
  const used = new Set(entries.map((e) => e.category));
  return {
    ...catalog,
    entries,
    categories: catalog.categories.filter((c) => used.has(c.key)),
  };
}
